import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap';

import { BookOpen } from 'lucide-react';
import { InlineMarkdown } from '@/components/core/markdown/markdown';

interface ConceptPopoverProps {
  /** Concept title */
  title: string;
  /** Concept description */
  description?: string;
  /** Position to display the popover */
  position: { x: number; y: number };
  /** Callback when popover should close */
  onClose: () => void;
  /** Whether the popover is visible */
  isVisible: boolean;
}

export function ConceptPopover({
  title,
  description,
  position,
  onClose,
  isVisible,
}: ConceptPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  // Animate in/out
  useEffect(() => {
    if (!popoverRef.current) return;

    if (isVisible) {
      gsap.fromTo(
        popoverRef.current,
        { opacity: 0, scale: 0.8, y: 10 },
        { opacity: 1, scale: 1, y: 0, duration: 0.25, ease: 'back.out(1.7)' }
      );
    }
  }, [isVisible]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    if (isVisible) {
      // Delay adding listener to avoid immediate close
      const timeout = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      return () => {
        clearTimeout(timeout);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isVisible, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  // Calculate position adjustments to keep popover in viewport
  const popoverWidth = 240;
  const popoverHeight = 120;
  const padding = 16;

  let adjustedX = position.x - popoverWidth / 2;
  let adjustedY = position.y - popoverHeight - 20; // Above the dot
  let showBelow = false;

  // Keep within viewport bounds
  if (typeof window !== 'undefined') {
    // Clamp X position
    if (adjustedX < padding) {
      adjustedX = padding;
    } else if (adjustedX + popoverWidth > window.innerWidth - padding) {
      adjustedX = window.innerWidth - popoverWidth - padding;
    }

    // Check if we need to show below
    if (adjustedY < padding) {
      showBelow = true;
      adjustedY = position.y + 30;
    }
  }

  // Calculate arrow position relative to popover
  const arrowX = Math.max(
    20,
    Math.min(popoverWidth - 20, position.x - adjustedX)
  );

  const popoverContent = (
    <div
      ref={popoverRef}
      className='fixed z-50 rounded-xl border border-border bg-card p-4 shadow-lg'
      style={{
        left: adjustedX,
        top: adjustedY,
        width: popoverWidth,
        maxWidth: `calc(100vw - ${padding * 2}px)`,
      }}
    >
      {/* Header with icon */}
      {/* `h3`, not `h4`. Its only mount is inside `KnowledgeArchiveCard`, whose
          own title is an `h2` under the page's `h1` — so `h4` skipped a level
          and failed axe's `heading-order` on the composed card. The standalone
          popover story passes either way, there being no other heading for it
          to be out of order with, which is exactly why the composed story is
          the one that found it. The level is the tag; the size is the class. */}
      <div className='mb-2 flex items-start gap-3'>
        <div className='shrink-0 rounded-lg bg-muted p-1.5'>
          <BookOpen className='h-4 w-4 text-muted-foreground' />
        </div>
        <h3 className='text-sm font-semibold leading-tight text-foreground'>
          <InlineMarkdown>{title}</InlineMarkdown>
        </h3>
      </div>

      {/* Description */}
      {description && (
        <p
          className={`line-clamp-3 text-xs leading-relaxed text-muted-foreground`}
        >
          <InlineMarkdown>{description}</InlineMarkdown>
        </p>
      )}

      {/* Arrow indicator - points to click position */}
      <div
        className={`absolute h-3 w-3 rotate-45 transform bg-card ${
          showBelow
            ? 'border-l border-t border-border'
            : 'border-b border-r border-border'
        }`}
        style={{
          [showBelow ? 'top' : 'bottom']: -6,
          left: arrowX,
          marginLeft: -6,
        }}
      />
    </div>
  );

  // Render into document.body via portal to avoid CSS transform issues
  return createPortal(popoverContent, document.body);
}
